/**
 * GraphQL Subscription Service
 *
 * Handles real-time subscriptions for notifications and data updates
 */

import { generateClient } from "aws-amplify/api";

/**
 * Global Subscribe Subscription
 *
 * GraphQL subscription for real-time global messages/events
 */
const GLOBAL_SUBSCRIBE_SUBSCRIPTION = `
  subscription GlobalSubscribe($EventId: String!, $Data: String, $Message: String, $MessageType: String, $Type: String) {
    globalsubscribe(EventId: $EventId, Data: $Data, Message: $Message, MessageType: $MessageType, Type: $Type) {
      EventId
      Message
      MessageType
      Type
      Data
    }
  }
`;

/**
 * Subscribe to global messages/events
 *
 * @param {string} eventId - Event ID to subscribe to (required)
 * @param {Function} onMessage - Callback function when message is received
 * @param {Function} onError - Callback function when error occurs
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGlobal = async (eventId, onMessage, onError) => {
  if (!eventId) {
    throw new Error("Event ID is required for subscription");
  }

  try {
    const client = generateClient();
    
    // Create subscription using Amplify's GraphQL API
    const subscription = client.graphql({
      query: GLOBAL_SUBSCRIBE_SUBSCRIPTION,
      variables: {
        EventId: eventId,
      },
    }).subscribe({
      next: ({ data }) => {
        if (data && data.globalsubscribe) {
          onMessage(data.globalsubscribe);
        }
      },
      error: (err) => {
        console.error("Subscription error:", err);
        if (onError) {
          onError(err);
        }
      },
    });

    // Return unsubscribe function
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  } catch (error) {
    console.error("Failed to create subscription:", error);
    if (onError) {
      onError(error);
    }
    // Return a no-op unsubscribe function if subscription fails
    return () => {};
  }
};

/**
 * Create a subscription hook for React components
 * 
 * Usage:
 * const unsubscribe = useGlobalSubscription('event-id', (message) => {
 *   console.log('Received:', message);
 * });
 * 
 * useEffect(() => {
 *   return () => {
 *     if (unsubscribe) unsubscribe();
 *   };
 * }, []);
 */
export const useGlobalSubscription = (eventId, onMessage, onError) => {
  let unsubscribe = null;

  const startSubscription = async () => {
    try {
      unsubscribe = await subscribeToGlobal(eventId, onMessage, onError);
    } catch (error) {
      console.error("Subscription setup failed:", error);
    }
  };

  const stopSubscription = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };

  return {
    start: startSubscription,
    stop: stopSubscription,
  };
};

const subscriptionService = {
  subscribeToGlobal,
  useGlobalSubscription,
};

export default subscriptionService;
